'use client';

import { useCallback } from 'react';
import { useAppDispatch, store } from '@/store/store';
import {
  bulkBatchProgressReceived,
  type BulkBatchesState,
} from '@/store/slices/bulkBatchesSlice';
import { radarBulkProgressReceived } from '@/store/slices/radarBulkUnlockSlice';
import { notifyBulkBatchCompleted } from '@/lib/utils/bulk-batch-completion.utils';

/**
 * The `bulk.progress` SSE payload, as Workflow-Service pushes it
 * (workflow-bulk.service.ts / phone-workflow-bulk.service.ts's
 * incrementBulkBatchCounts):
 *
 *   { workflowId, category, at, event: "bulk.progress",
 *     status: "in_progress" | "completed",
 *     completed, total,
 *     counts: { completed, found, notFound, creditExhausted } }
 *
 * Two things are worth being explicit about, because they shape every
 * consumer of this event:
 *
 *  - `workflowId` is the BATCH's id, not an item's. The event is aggregate
 *    only - there is no `input`, no personId, no docId, nothing that says
 *    which row moved. Anything that needs per-row detail has to get it from
 *    a refetch.
 *  - It is pushed every 5 completions, plus always once at the end with
 *    `status: "completed"` even when `total` isn't a multiple of 5. For a
 *    batch of MORE than 100 accepted items it is the only live event of any
 *    kind (Workflow-Service's SSE_SILENT_ITEM_THRESHOLD suppresses every
 *    per-item push above that size), so for large runs this is the sole
 *    thing standing between the user and a frozen progress bar.
 */
export interface BulkProgressEvent {
  workflowId?: unknown;
  category?: unknown;
  event?: unknown;
  status?: unknown;
  completed?: unknown;
  total?: unknown;
  counts?: {
    completed?: unknown;
    found?: unknown;
    notFound?: unknown;
    creditExhausted?: unknown;
  };
}

/** Narrowed, validated form of the event - what every consumer actually reads. */
export interface NormalizedBulkProgress {
  batchId: string;
  category: 'email' | 'phone';
  completed: number;
  total: number;
  counts: { completed: number; found: number; notFound: number; creditExhausted: number };
  isFinal: boolean;
}

const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * Validates a raw `bulk.progress` payload. Returns null for anything
 * unusable, so a malformed push can't create a phantom batch card stuck at
 * 0/0 in the notification panel.
 */
export function normalizeBulkProgress(p: BulkProgressEvent): NormalizedBulkProgress | null {
  const batchId = typeof p.workflowId === 'string' ? p.workflowId : '';
  if (!batchId) return null;
  const category: 'email' | 'phone' = p.category === 'phone' ? 'phone' : 'email';
  const completed = num(p.completed, num(p.counts?.completed));
  const total = num(p.total);
  return {
    batchId,
    category,
    completed,
    total,
    counts: {
      completed: num(p.counts?.completed, completed),
      found: num(p.counts?.found),
      notFound: num(p.counts?.notFound),
      creditExhausted: num(p.counts?.creditExhausted),
    },
    isFinal: p.status === 'completed',
  };
}

/**
 * Routes one `bulk.progress` event to the two consumers that want it
 * regardless of which screen is mounted:
 *
 *  - the notification panel's batch list (bulkBatchesSlice), which tracks
 *    every running batch of this user including ones this session never
 *    started;
 *  - People Search's own progress pills (radarBulkUnlockSlice), which use it
 *    as the floor under their per-contact derivation and no longer have to
 *    wait on the 5s poll to move.
 *
 * SCOPED TO PEOPLE SEARCH. Workspace column enrichments produce these events
 * too - Enrichment-Service's /workspace/sendBulkEmail posts to the very same
 * Workflow-Service bulk endpoint, just with source "workspace" - but nothing
 * consumes them yet, and they're filtered out of the panel (see
 * selectBulkBatches). That leaves a known gap: a Workspace sheet over 100 rows
 * gets no per-item events either (Workflow-Service's bulkSilent), so its
 * column header progress stays at 0/N and its rows are never refetched. That
 * bug predates this hook; wiring Workspace up here is the fix when it's wanted.
 *
 * Unlike the `workflow.progress` handlers, this one does NOT claim the event:
 * `bulk.progress` has no per-workflow path to fall through to.
 */
export function useBulkBatchProgressEvents() {
  const dispatch = useAppDispatch();

  return useCallback(
    (raw: BulkProgressEvent): NormalizedBulkProgress | null => {
      const p = normalizeBulkProgress(raw);
      if (!p) return null;

      dispatch(
        bulkBatchProgressReceived({
          batchId: p.batchId,
          category: p.category,
          completed: p.completed,
          total: p.total,
          counts: p.counts,
          isFinal: p.isFinal,
        })
      );

      // A no-op unless one of People Search's open batches has this id, which
      // is the common case when the event belongs to Workspace instead.
      dispatch(
        radarBulkProgressReceived({
          bulkBatchId: p.batchId,
          completed: p.completed,
          total: p.total,
          isFinal: p.isFinal,
        })
      );

      // A finished batch is the moment the contact lists become stale, and for
      // a background run nothing else will notice: it emits no per-item events
      // and has usually outlived the screen that started it. Read AFTER the
      // dispatch above so the batch's own `source` is present for a run this
      // session registered at submit time.
      if (p.isFinal) {
        const key = `${p.category}:${p.batchId}`;
        const batch = (store.getState() as { bulkBatches: BulkBatchesState }).bulkBatches.byKey[key];
        notifyBulkBatchCompleted(dispatch, batch, key);
      }

      return p;
    },
    [dispatch]
  );
}
